import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { mdsSidebarWidths, PanelMode } from "@/design-system/foundations";
import { SidebarVariant } from "@/components/layout/components/sidebar/constants";
import { computed, makeObservable } from "mobx";

export class AppStoreLayoutStore extends AppSubStore {
  constructor(args: AppSubStoreArgs) {
    super(args);
    makeObservable<this>(this, {
      hasRoomForSidebarOnInitialLoad: computed,
      shouldHideSidebarToggle: computed,
      currentSidebarVariant: computed,
    });
  }

  get hasRoomForSidebarOnInitialLoad(): boolean {
    const viewportWidth = this.store.interface.dimensionsState.data?.width ?? 0;
    if (viewportWidth === 0) return false;
    const expanded = mdsSidebarWidths().expanded;
    const centerAfterOpen = viewportWidth - expanded;
    const minContainer = this.store.navigation.tabs.MIN_CONTAINER_WIDTH;
    const twoPanes = this.store.navigation.tabs.panes.length >= 2;
    const hasRoom = !twoPanes || centerAfterOpen >= minContainer;
    return hasRoom;
  }

  get shouldHideSidebarToggle(): boolean {
    const breakpoints = this.store.interface.matchesPanelModeBreakpoint;
    const matchesNarrowView = !!breakpoints.get(PanelMode.Narrow);

    const viewportWidth = this.store.interface.dimensionsState.data?.width ?? 0;
    const expanded = mdsSidebarWidths().expanded;
    const centerAfterOpen = viewportWidth - expanded;
    const minContainer = this.store.navigation.tabs.MIN_CONTAINER_WIDTH;
    const twoPanes = this.store.navigation.tabs.panes.length >= 2;
    const narrowMultiPane = twoPanes && centerAfterOpen < minContainer;

    // Hide when very narrow overall OR when two center panes would become too small if sidebar is shown
    return matchesNarrowView || narrowMultiPane;
  }

  get currentSidebarVariant(): SidebarVariant {
    const breakpoints = this.store.interface.matchesPanelModeBreakpoint;
    const matchesNarrowView = !!breakpoints.get(PanelMode.Narrow);
    const matchesWideSinglePanel = !!breakpoints.get(PanelMode.WideSinglePanel);
    const matchesWideSplitView = !!breakpoints.get(PanelMode.WideSplitView);
    const isSplitView = this.store.sidePanel.isSplitView;
    const isSidebarOpen = this.store.interface.isSidebarOpen;
    if (matchesNarrowView) return SidebarVariant.Hidden;
    const isWideEnough = isSplitView ? matchesWideSplitView : matchesWideSinglePanel;
    return isWideEnough && isSidebarOpen ? SidebarVariant.Expanded : SidebarVariant.Collapsed;
  }
}
